import React, { useEffect, useRef } from 'react';
import { Panel } from 'primereact/panel';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';
import { useMutation, useQuery } from '@tanstack/react-query';
import { downloadFile } from '../../../../../utils/fileUtil';
import { ContractAttachment } from '../../../../Interfaces/Accounting/ContractBillings.interface';
import { fetchContractDraftAttachment } from '../../../../services/accounting/ACFormsService';
import useFetchAttachments from '../../../../hooks/useFetchAttachments';
import ErrorToast, {
  showToast,
} from '../../../../components/messages/ErrorAlert';
import {
  fetchContractGeneralInfo,
  fetchContractLookUpInfo,
  fetchJobLookUpInfo,
} from '../../../../services/accounting/ACFormsService';
import { useCompanyContext } from '../../../../context/CompanyContext';
import ErrorMessage from '../../../../components/messages/ErrorMessage';
import {
  formatCurrency,
  formatDate,
  formatPercentage,
} from '../../../../../utils/formatUtils';
import { fetchContractHistory } from '@/src/app/services/accounting/ContractBillingsService';

export const stateLabel: Record<string, string> = {
  in_process: 'In Process',
  success: 'Success',
  failure: 'Failed',
};

type ContractDrilldownDataProps = {
  id: string;
};

const ContractDrilldownData = ({ id }: ContractDrilldownDataProps) => {
  const { selectedCompany } = useCompanyContext();
  const toast = useRef<Toast>(null);

  const { data, isFetching, isError } = useQuery({
    queryKey: ['getContractByFormHistory', id],
    queryFn: ({ signal }) => fetchContractHistory(id, signal),
    refetchOnWindowFocus: false,
    enabled: !!selectedCompany,
  });
  const { fetchAttachments, loading, error } = useFetchAttachments({
    uniqueAttchID: data?.uniqueAttchID ?? '',
    zipFilename: data?.contractName ?? data?.contract ?? '',
  });

  const attachmentRequest = useMutation({
    mutationFn: (attachment: ContractAttachment) =>
      fetchContractDraftAttachment(id, attachment.uniqueName ?? ''),
    onSuccess: (response, attachment) => {
      downloadFile(response, attachment.name);
    },
    onError: () => {
      showToast(
        'error',
        toast,
        'Download Error',
        "The attachment couldn't be downloaded",
        3000
      );
    },
  });

  const contractOptionsData = useQuery({
    queryKey: ['getNewContractInfo', selectedCompany],
    queryFn: ({ signal }) =>
      fetchContractLookUpInfo(selectedCompany!.id, signal),
    refetchOnWindowFocus: false,
    enabled: !!selectedCompany,
  });

  const jobOptionsData = useQuery({
    queryKey: ['getNewJobInfo', selectedCompany, data?.department],
    queryFn: ({ signal }) =>
      fetchJobLookUpInfo(selectedCompany!.id, data.department, signal),
    refetchOnWindowFocus: false,
    enabled: !!(selectedCompany && data?.department),
  });

  const generalOptionsData = useQuery({
    queryKey: ['getContractGeneralInfo', selectedCompany],
    queryFn: ({ signal }) =>
      fetchContractGeneralInfo(selectedCompany!.id, signal),
    refetchOnWindowFocus: false,
    enabled: !!selectedCompany,
  });

  useEffect(() => {
    if (error) {
      showToast('error', toast, 'Download Error', error, 3000);
    }
  }, [error]);

  if (
    isFetching ||
    contractOptionsData.isFetching ||
    jobOptionsData.isFetching ||
    generalOptionsData.isFetching
  ) {
    return (
      <div className="flex justify-content-center mt-4">
        <ProgressSpinner />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <ErrorMessage content="Failed to load contract details. Please try again later." />
    );
  }

  const isDownloading = (attachment: ContractAttachment) =>
    attachmentRequest.isPending &&
    attachmentRequest.variables?.uniqueName === attachment.uniqueName;

  return (
    <div className="flex flex-column gap-3 mx-3">
      <div className="grid m-0 blackText text-standard">
        <div className="col-12">
          <label className="text-18px block mb-2 font-bold">Status:</label>
          {stateLabel[data.draftState] ?? data.draftState}
        </div>
      </div>
      <Panel header="Contract Info" toggleable>
        <div className="grid mx-3 blackText text-standard">
          <div className="col-12 sm:col-6">
            <label className="text-18px block mb-2 font-bold">Contract:</label>
            {data.contract ?? '-'}
          </div>
          <div className="col-12 sm:col-6">
            <label className="text-18px block mb-2 font-bold">
              Contract Name:
            </label>
            {data.contractName ?? '-'}
          </div>
          <div className="col-12 sm:col-6">
            <label className="text-18px block mb-2 font-bold">
              Department:
            </label>
            {contractOptionsData.data?.departments.find(
              (dep: { Department: string; Description: string }) =>
                dep.Department === `${data.department}`
            )?.Description ?? '-'}
          </div>
          <div className="col-12 sm:col-6">
            <label className="text-18px block mb-2 font-bold">Customer:</label>
            {contractOptionsData.data?.customers.find(
              (cust: { name: string; accountNumber: string }) =>
                cust.accountNumber === data.customer
            )?.name ?? '-'}
          </div>
          <div className="col-12 sm:col-6">
            <label className="text-18px block mb-2 font-bold">
              Retainage Default:
            </label>
            {data.retainage != null ? formatPercentage(data.retainage) : '-'}
          </div>
          {data.processGroup && (
            <div className="col-12 sm:col-6">
              <label className="text-18px block mb-2 font-bold">
                Processing Group:
              </label>
              {contractOptionsData.data?.processGroups.find(
                (group: { ProcessGroup: string; Description: string }) =>
                  group.ProcessGroup === data.processGroup
              )?.Description ?? 'TBD'}
            </div>
          )}
          <div className="col-12 sm:col-6">
            <label className="text-18px block mb-2 font-bold">
              Start Date:
            </label>
            {data.startDate ? formatDate(new Date(data.startDate)) : '-'}
          </div>
        </div>
      </Panel>
      <Panel header="Job Info" toggleable>
        <div className="grid mx-3 blackText text-standard">
          <div className="col-12 sm:col-6">
            <label className="text-18px block mb-2 font-bold">
              Project Manager:
            </label>
            {jobOptionsData.data?.projectManagers.find(
              (manager: { ProjectMgr: string; Name: string }) =>
                manager.ProjectMgr === data.projectManager
            )?.Name ?? '-'}
          </div>
          <div className="col-12 sm:col-6">
            <label className="text-18px block mb-2 font-bold">Address 1:</label>
            {data.address1 ?? '-'}
          </div>
          {data.address2 && (
            <div className="col-12 sm:col-6">
              <label className="text-18px block mb-2 font-bold">
                Address 2:
              </label>
              {data.address2}
            </div>
          )}
          <div className="col-12 sm:col-6">
            <label className="text-18px block mb-2 font-bold">City:</label>
            {data.city ?? '-'}
          </div>
          <div className="col-12 sm:col-6">
            <label className="text-18px block mb-2 font-bold">State:</label>
            {jobOptionsData.data?.states.find(
              (state: { State: string; Name: string }) =>
                state.State === data.state
            )?.Name ?? '-'}
          </div>
          <div className="col-12 sm:col-6">
            <label className="text-18px block mb-2 font-bold">ZIP:</label>
            {data.zip ?? '-'}
          </div>
          <div className="col-12 sm:col-6">
            <label className="text-18px block mb-2 font-bold">County:</label>
            {data.county ?? '-'}
          </div>
          <div className="col-12 sm:col-6">
            <label className="text-18px block mb-2 font-bold">
              Payroll State:
            </label>
            {jobOptionsData.data?.states.find(
              (state: { State: string; Name: string }) =>
                state.State === data.prState
            )?.Name ?? '-'}
          </div>
          <div className="col-12 sm:col-6">
            <label className="text-18px block mb-2 font-bold">
              Sales Tax Code:
            </label>
            {jobOptionsData.data?.taxCodes.find(
              (tax: { TaxCode: string; Description: string }) =>
                tax.TaxCode === data.taxCode
            )?.Description ?? '-'}
          </div>
          {data.reviewerGroup && (
            <div className="col-12 sm:col-6">
              <label className="text-18px block mb-2 font-bold">
                AP Inv Rev Grp:
              </label>
              {jobOptionsData.data?.reviewerGroups.find(
                (group: { ReviewerGroup: string; Description: string }) =>
                  group.ReviewerGroup === data.reviewerGroup
              )?.Description ?? '-'}
            </div>
          )}
          {jobOptionsData.data?.allowExtraJob && (
            <div className="col-12 sm:col-6">
              <label className="text-18px block mb-2 font-bold">
                Create service maintenance extra job?
              </label>
              {data.extraJob ? 'Yes' : 'No'}
            </div>
          )}
          <div className="col-12">
            <label className="text-18px block mb-2 font-bold">
              Panel of Physicians Requested:
            </label>
            {data.physician ?? '-'}
          </div>
          <div className="col-12">
            <label className="text-18px block mb-2 font-bold">
              Notice to Owner:
            </label>
            {data.notice ?? '-'}
          </div>
        </div>
      </Panel>
      <Panel header="General Info" toggleable>
        <div className="grid mx-3 blackText text-standard">
          <div className="col-12 sm:col-6">
            <label className="text-18px block mb-2 font-bold">
              Estimated Bond Cost:
            </label>
            {data.bondCost != null ? formatCurrency(data.bondCost) : '-'}
          </div>
          <div className="col-12 sm:col-6">
            <label className="text-18px block mb-2 font-bold">
              Bond Percentage:
            </label>
            {data.bondPercentage != null
              ? formatPercentage(data.bondPercentage)
              : '-'}
          </div>
          <div className="col-12 sm:col-6">
            <label className="text-18px block mb-2 font-bold">
              Bond Ordered:
            </label>
            {data.bondReqd ?? '-'}
          </div>
          {data.dateOrdered && (
            <div className="col-12 sm:col-6">
              <label className="text-18px block mb-2 font-bold">
                Bond Date Ordered:
              </label>
              {formatDate(new Date(data.dateOrdered))}
            </div>
          )}
          <div className="col-12 sm:col-6">
            <label className="text-18px block mb-2 font-bold">
              Insurance Cert Ordered:
            </label>
            {data.insurance ?? '-'}
          </div>
          {data.insuranceDateOrdered && (
            <div className="col-12 sm:col-6">
              <label className="text-18px block mb-2 font-bold">
                Insurance Date Ordered:
              </label>
              {formatDate(new Date(data.insuranceDateOrdered))}
            </div>
          )}
          <div className="col-12 sm:col-6">
            <label className="text-18px block mb-2 font-bold">
              Contract Value:
            </label>
            {data.contractValue != null
              ? formatCurrency(data.contractValue)
              : '-'}
          </div>
          <div className="col-12 sm:col-6">
            <label className="text-18px block mb-2 font-bold">
              Supervision & OH:
            </label>
            {data.supervision != null ? formatCurrency(data.supervision) : '-'}
          </div>
          <div className="col-12 sm:col-6">
            <label className="text-18px block mb-2 font-bold">
              Target Job Cost:
            </label>
            {data.targetCost != null ? formatCurrency(data.targetCost) : '-'}
          </div>
          {data.estProfit != null && (
            <div className="col-12 sm:col-6">
              <label className="text-18px block mb-2 font-bold">
                Estimated Profit:
              </label>
              {formatCurrency(data.estProfit)}
            </div>
          )}
          {data.estProfitPercentage != null && (
            <div className="col-12 sm:col-6">
              <label className="text-18px block mb-2 font-bold">
                Estimated Profit %:
              </label>
              {formatPercentage(data.estProfitPercentage)}
            </div>
          )}
          {data.estCostMarkUp != null && (
            <div className="col-12 sm:col-6">
              <label className="text-18px block mb-2 font-bold">
                Estimated Cost Mark Up %:
              </label>
              {formatPercentage(data.estCostMarkUp)}
            </div>
          )}
          <div className="col-12 sm:col-6">
            <label className="text-18px block mb-2 font-bold">Job Type:</label>
            {data.jobType ?? '-'}
          </div>
          <div className="col-12 sm:col-6">
            <label className="text-18px block mb-2 font-bold">
              Certified Payroll:
            </label>
            {data.certified ?? '-'}
          </div>
          <div className="col-12 sm:col-6">
            <label className="text-18px block mb-2 font-bold">CCIP/OCIP:</label>
            {data.ccip ?? '-'}
          </div>
          <div className="col-12 sm:col-6">
            <label className="text-18px block mb-2 font-bold">
              Dwelling Units:
            </label>
            {data.dwellingUnits ?? '-'}
          </div>
          <div className="col-12 sm:col-6">
            <label className="text-18px block mb-2 font-bold">Metric 1:</label>
            {data.metric1 ?? '-'}
          </div>
          <div className="col-12 sm:col-6">
            <label className="text-18px block mb-2 font-bold">
              Metric 1 Amt:
            </label>
            {data.metric1Amt ?? '-'}
          </div>
          <div className="col-12 sm:col-6">
            <label className="text-18px block mb-2 font-bold">Metric 2:</label>
            {data.metric2 ?? '-'}
          </div>
          <div className="col-12 sm:col-6">
            <label className="text-18px block mb-2 font-bold">
              Metric 2 Amt:
            </label>
            {data.metric2Amt ?? '-'}
          </div>
          <div className="col-12 sm:col-6">
            <label className="text-18px block mb-2 font-bold">
              Heated Sq Ft:
            </label>
            {data.heated ?? '-'}
          </div>
          <div className="col-12 sm:col-6">
            <label className="text-18px block mb-2 font-bold">
              Parking Deck:
            </label>
            {data.parking ?? '-'}
          </div>
          {data.fieldPS && (
            <div className="col-12 sm:col-6">
              <label className="text-18px block mb-2 font-bold">
                Field PM/Super:
              </label>
              {generalOptionsData.data?.employees.find(
                (emp: { Name: string; SortName: string }) =>
                  emp.SortName === data.fieldPS
              )?.Name ?? data.fieldPS}
            </div>
          )}
        </div>
      </Panel>
      <Panel header="Attachments" toggleable>
        <ErrorToast toastRef={toast} />
        <div className="mx-3 text-standard blackText">
          {data.attachments?.length > 0 ? (
            loading ? (
              <ProgressSpinner className="w-1rem h-auto" />
            ) : (
              <ul
                className={`m-0 pl-3 ${
                  data.uniqueAttchID
                    ? 'totalColor cursor-pointer hover:underline'
                    : ''
                }`}
                onClick={
                  data.uniqueAttchID ? () => fetchAttachments() : undefined
                }
              >
                {data.attachments.map((attachment: ContractAttachment) => (
                  <li key={attachment.uniqueName ?? attachment.name}>
                    {isDownloading(attachment) ? (
                      <ProgressSpinner className="w-1rem h-auto" />
                    ) : attachment.uniqueName ? (
                      <span
                        className="totalColor cursor-pointer hover:underline"
                        onClick={() => attachmentRequest.mutate(attachment)}
                      >
                        {attachment.name}
                      </span>
                    ) : (
                      attachment.name
                    )}
                  </li>
                ))}
              </ul>
            )
          ) : (
            'No attachments were provided.'
          )}
        </div>
      </Panel>
    </div>
  );
};

export default ContractDrilldownData;
